<script lang="ts" setup>
import { useElementSize, useScroll, useWindowScroll } from '@vueuse/core';
import { useIsPopup } from '~/composables/popup/usePopupCallback';

const isPopup = useIsPopup();
const { chromeV2 } = useChromeVersion();

// Déclinaison mobile CD (composants Mobile/*) : au format mobile ET feature `mobile` ouverte, on
// monte le shell mobile plein écran (MobileApp) à la place du chrome desktop (cf. useMobileShell).
// `windowScrolls` distingue les deux contrats de scroll qui cohabitent sous le chrome v2 : un écran
// v2 laisse défiler la fenêtre (sticky du header), un écran legacy veut le cadre viewport du layout
// legacy — cf. `.gd-app--framed` dans assets/styles/ds/overrides.css.
const { showMobileShell, windowScrolls, bpClasses } = useMobileShell();

// Chrome v2 : topbar et onglets dossier vivent DANS le même bloc `.gd-header` sticky (structure de
// app.jsx). Le navpanel dossier est donc monté ici et non par la page dossier. Question de route
// seule : le layout est monté partout, les onglets eux ouvrent la query du dossier.
const inDossier = useInDossier();

// `.gd-header.scrolled` = ombre + divider, une fois le contenu passé dessous (seuil de 2px comme
// l'amont). La source de scroll suit `windowScrolls` : sous `.gd-app--framed`, la fenêtre est en
// `overflow: hidden` et c'est le conteneur de page qui défile. Un écran legacy qui scrolle DANS ses
// propres panneaux ne passe rien sous le header : pas d'ombre, et c'est le rendu attendu.
const framedPage = ref<HTMLElement | null>(null);
const { y: windowScrollY } = useWindowScroll();
const { y: pageScrollY } = useScroll(framedPage);
const headerScrolled = computed<boolean>(() =>
  (windowScrolls.value ? windowScrollY.value : pageScrollY.value) > 2);

const header = ref<HTMLElement | null>(null);
const { height: headerHeight } = useElementSize(header, undefined, { box: 'border-box' });
const headerHeightVar = computed<Record<string, string> | undefined>(() =>
  (headerHeight.value > 0 ? { '--gd-header-h': `${headerHeight.value}px` } : undefined));
</script>

<template>
  <div
    v-if="isPopup"
    class="min-h-screen bg-surface-50 dark:bg-surface-950"
  >
    <slot />
  </div>
  <!-- MobileApp est un CHROME, pas un routeur : la page routée DOIT lui être passée en enfant,
       il la rend dans sa zone scrollable. Auto-fermé (`<MobileApp />`), son `<slot />` restait
       vide et le mobile affichait un shell sans contenu. -->
  <MobileApp v-else-if="showMobileShell">
    <slot />
  </MobileApp>
  <div
    v-else-if="chromeV2"
    :class="['gd-app', ...bpClasses, { 'gd-app--framed': !windowScrolls }]"
    :style="headerHeightVar"
  >
    <DsGlobalTooltip />
    <LayoutV2Sidebar />
    <main class="gd-main">
      <div
        ref="header"
        :class="['gd-header', { scrolled: headerScrolled }]"
      >
        <LayoutV2Topbar />
        <div
          v-if="inDossier"
          class="gd-navpanel gd-navpanel-inline"
        >
          <LayoutV2SubTabs />
        </div>
      </div>
      <!-- Un seul conteneur pour les deux modes, seule la classe change : deux branches
           `v-if`/`v-else` démonteraient la page à chaque bascule (les deux versions se croisent en
           navigation interne au dossier). Écran legacy : `.gd-app--framed` cadre l'app à la hauteur
           du viewport, ce conteneur porte le scroll de page. Écran v2 : il passe en
           `display: contents` (overrides.css), la page redevient enfant direct de `.gd-main`. -->
      <div
        ref="framedPage"
        :class="['gd-page-host', { 'gd-page--framed': !windowScrolls }]"
      >
        <slot />
      </div>
    </main>
  </div>
  <div
    v-else
    class="min-h-screen flex relative lg:static bg-surface-50 dark:bg-surface-950"
  >
    <LayoutDefaultSideBarComponent />
    <div class="h-screen w-full max-h-screen min-h-screen flex flex-col relative flex-auto overflow-hidden">
      <LayoutDefaultTopBarComponent />
      <div class="flex flex-col flex-auto overflow-hidden">
        <LayoutDefaultBreadCrumbComponent />
        <div class="border border-surface bg-surface-0 dark:bg-surface-950 flex-auto inline-flex overflow-auto">
          <slot />
        </div>
      </div>
    </div>
  </div>
</template>
